import { Rule } from 'antd/es/form'
import { isValidPhoneNumber } from 'libphonenumber-js'
import { AttachmentType } from './AttachmentFile'
import { phoneMetadataToString } from './PhoneFormatter'

export const integerRule: Rule = {
  type: 'integer',
  message: 'The input is not a valid number',
}

export const minLengthRule = function (length: number): Rule {
  return {
    pattern: new RegExp(`.{${length.toString()},}`, 'g'),
    message: `The value must have at least ${length} characters`,
  }
}

export const capsRule = function (): Rule {
  return {
    pattern: new RegExp(/[A-Z]+/g),
    message: 'The value must include a capital letter',
  }
}
export const lowercaseRule = function (): Rule {
  return {
    pattern: new RegExp(/[a-z]+/g),
    message: 'The value must contain at least one lowercase letter',
  }
}

export const containsNumberRule = function (): Rule {
  return {
    pattern: new RegExp(/\d+/g),
    message: 'The value must have at least one number',
  }
}
export const containsSymbolRule = function (): Rule {
  return {
    pattern: new RegExp(/\W+/g),
    message: 'The value must have at least one symbol',
  }
}

export const numberRule = function (): Rule {
  return {
    type: 'number',
    message: 'Input is not a valid number',
  }
}

export const stringRule = function (): Rule {
  return {
    type: 'string',
    message: 'Input is not a valid string',
  }
}

export const versionRule = function (): Rule {
  return {
    pattern: new RegExp(/^\d+\.\d+\.\d+$/),
    message:
      'The version must be in the format X.Y.Z where X, Y, and Z are numbers. Example : 212.32.3',
  }
}

export const urlRule = function (): Rule {
  return {
    type: 'url',
    message: 'Input is not a valid link',
  }
}

export const emailRule = function (): Rule {
  return {
    type: 'email',
    message: 'Input is not a valid email',
  }
}

export const requiredRule = function (): Rule {
  return {
    required: true,
    message: 'This field is required',
  }
}

export const siretRule = function (): Rule {
  return {
    required: true,
    pattern: /^\d{1,14}$/,
    message: 'SIRET must be at most 14 digits',
  }
}

export const acceptedRule = function (): Rule {
  return {
    validator(_, value) {
      if (value) {
        return Promise.resolve()
      }
      return Promise.reject('This field is required')
    },
  }
}

export const nestedCascaderExceptAddressRule = function (
  minDepth: number = 2
): Rule {
  return {
    validator(_, value) {
      if (value && value.length === 1 && value[0] === 'customaddress') {
        return Promise.resolve()
      }
      return value.length >= minDepth
        ? Promise.resolve()
        : Promise.reject('This field is required')
    },
  }
}

export const matchValueRule = (expectedValue: string): Rule => ({
  validator(_rule: any, value: string) {
    if (value === expectedValue) {
      return Promise.resolve()
    }
    return Promise.reject(new Error('The value mismatch the expected value'))
  },
})

export const notMatchValuesRule = (reserved: string[]): Rule => ({
  validator(_rule: any, value: string) {
    if (reserved.includes(value)) {
      return Promise.reject(new Error('This value is reserved'))
    }
    return Promise.resolve()
  },
})

export const lessOneIsChecked = (array: any[]): Rule => ({
  validator(_rule: any, value: string) {
    if (array.length > 0) {
      return Promise.resolve()
    }
    return Promise.reject(new Error('The value mismatch the expected value'))
  },
})

export const isE164Phone = function (): Rule {
  return {
    required: true,
    validator: (_, value) => {
      if (!value) {
        return Promise.resolve()
      }

      if (typeof value === 'string') {
        if (!isValidPhoneNumber(value)) {
          return Promise.reject(new Error('Invalid phone number format'))
        }

        return Promise.resolve()
      }

      var phone = ''
      if ('typed' in value) {
        phone = value.typed
      } else {
        phone = phoneMetadataToString(value)
      }

      if (!isValidPhoneNumber(phone)) {
        return Promise.reject(new Error('Invalid phone number format'))
      }

      return Promise.resolve()
    },
  }
}

export const doesntExceedSize = function (
  attachmentTypes: AttachmentType[]
): Rule {
  return {
    validator: (_, value) => {
      if (!value || !Array.isArray(value)) {
        return Promise.resolve()
      }

      const file = value[value.length - 1]
      if (!file) {
        return Promise.resolve()
      }

      const attachmentType = attachmentTypes.find((at) =>
        at.accept.includes(file.type)
      )

      return attachmentType && file.size > attachmentType.maxSize
        ? Promise.reject(new Error('One or more file are too large'))
        : Promise.resolve()
    },
  }
}

export const afterFieldRule = (
  fieldName: string,
  message = 'End date must be after start date'
) => ({
  getFieldValue,
}: any) => ({
  validator(_: unknown, value: any) {
    const other = getFieldValue(fieldName)
    if (!value || !other || value.isAfter(other)) {
      return Promise.resolve()
    }
    return Promise.reject(new Error(message))
  },
})

export const confirmationRequiredRule = (): Rule => ({
  validator: (_, value) =>
    value
      ? Promise.resolve()
      : Promise.reject(
          new Error(
            ' You must validate the venue addition or modification policy.'
          )
        ),
})

export const gtmContainerIdRule = function (): Rule {
  return {
    validator: (_, value) => {
      if (!value) {
        return Promise.resolve()
      }
      return /^GTM-[A-Z0-9]{6,}$/.test(value)
        ? Promise.resolve()
        : Promise.reject(
            new Error('Invalid GTM container id (expected GTM-XXXXXXX)')
          )
    },
  }
}

// Mirror back-office-api #287 ProviderUrl: https scheme + host allow-list,
// path-agnostic (playlist, artist, album, track all accepted). Client and API
// reject identically.
const PROVIDER_HOSTS: Record<'spotify' | 'soundcloud', string[]> = {
  spotify: ['open.spotify.com'],
  soundcloud: [
    'soundcloud.com',
    'www.soundcloud.com',
    'm.soundcloud.com',
    'on.soundcloud.com',
  ],
}

const providerUrlRule = (
  provider: 'spotify' | 'soundcloud',
  message: string
): Rule => ({
  validator: (_, value) => {
    if (!value) {
      return Promise.resolve()
    }
    let ok = false
    try {
      const url = new URL(value)
      ok =
        url.protocol === 'https:' &&
        PROVIDER_HOSTS[provider].includes(url.hostname.toLowerCase())
    } catch {
      ok = false
    }
    return ok ? Promise.resolve() : Promise.reject(new Error(message))
  },
})

export const spotifyUrlRule = (): Rule =>
  providerUrlRule(
    'spotify',
    'Invalid Spotify URL (expected https://open.spotify.com/...)'
  )

export const soundcloudUrlRule = (): Rule =>
  providerUrlRule(
    'soundcloud',
    'Invalid SoundCloud URL (expected https://soundcloud.com/...)'
  )
